import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import type { Level, Unit } from '@/types'
import {
  Plus,
  Edit,
  Trash2,
  Search,
  ChevronDown,
  ChevronRight,
  BookOpen
} from 'lucide-react'

interface LevelWithUnits extends Level {
  units: Unit[]
}

export default function LevelsList() {
  const [levels, setLevels] = useState<LevelWithUnits[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [expandedLevels, setExpandedLevels] = useState<Set<string>>(new Set())
  const [deleteModal, setDeleteModal] = useState<{ open: boolean; level: Level | null }>({
    open: false,
    level: null
  })
  const [isDeleting, setIsDeleting] = useState(false)

  useEffect(() => {
    loadLevels()
  }, [])

  const loadLevels = async () => {
    try {
      const { data: levelsData, error: levelsError } = await supabase
        .from('levels')
        .select('*')
        .order('order_num', { ascending: true })

      if (levelsError) throw levelsError

      const { data: unitsData } = await supabase
        .from('units')
        .select('*')
        .order('order_num', { ascending: true })

      const levelsWithUnits = (levelsData || []).map((level: Level) => ({
        ...level,
        units: (unitsData || []).filter((u: Unit) => u.level_id === level.id)
      }))

      setLevels(levelsWithUnits)
      setExpandedLevels(new Set(levelsWithUnits.map((l: Level) => l.id)))
    } catch (error) {
      console.error('Error loading levels:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const toggleLevel = (levelId: string) => {
    const newExpanded = new Set(expandedLevels)
    newExpanded.has(levelId) ? newExpanded.delete(levelId) : newExpanded.add(levelId)
    setExpandedLevels(newExpanded)
  }

  const handleDelete = async () => {
    if (!deleteModal.level) return

    setIsDeleting(true)
    try {
      const { error } = await supabase
        .from('levels')
        .delete()
        .eq('id', deleteModal.level.id)

      if (error) throw error

      setLevels(levels.filter(l => l.id !== deleteModal.level?.id))
      setDeleteModal({ open: false, level: null })
    } catch (error) {
      console.error('Error deleting level:', error)
      alert('Error al eliminar el nivel.')
    } finally {
      setIsDeleting(false)
    }
  }

  const filteredLevels = levels.filter(level =>
    level.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    level.description?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0055A4]" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between">
        <div>
          <h1 className="text-2xl font-bold">Niveles</h1>
        </div>
        <Link to="/admin/levels/new" className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded">
          <Plus className="w-4 h-4" /> Nuevo
        </Link>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-2 top-2 w-4 h-4" />
        <input
          type="text"
          className="pl-8 border p-2 w-full"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* List */}
      {filteredLevels.map(level => (
        <div key={level.id} className="border rounded">
          <div
            className="flex justify-between p-3 cursor-pointer"
            role="button"
            tabIndex={0}
            onClick={() => toggleLevel(level.id)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleLevel(level.id) }}
          >
            <div className="flex gap-2 items-center">
              {expandedLevels.has(level.id) ? <ChevronDown /> : <ChevronRight />}
              <span>{level.name}</span>
            </div>

            <div className="flex gap-2">
              <Link to={`/admin/levels/${level.id}/edit`}>
                <Edit />
              </Link>
              <button onClick={(e) => { e.stopPropagation(); setDeleteModal({ open: true, level }) }}>
                <Trash2 />
              </button>
            </div>
          </div>

          {expandedLevels.has(level.id) && (
            <div className="p-3 bg-gray-50">
              {level.units.map(unit => (
                <div key={unit.id} className="flex items-center gap-2">
                  <BookOpen className="w-4 h-4" />
                  <span>{unit.title}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}

      {/* Modal */}
      {deleteModal.open && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50">
          <div className="bg-white p-4 rounded">
            <p>Eliminar nivel {deleteModal.level?.name}?</p>
            <button onClick={handleDelete} disabled={isDeleting}>
              Eliminar
            </button>
            <button onClick={() => setDeleteModal({ open: false, level: null })}>
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  )
}